import { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa"; // Import Font Awesome chevron and clear icons

const SelectInputClient = ({
	id,
	placeholder,
	value,
	onChange,
	search,
	options = [],
	label,
	disabled = false,
	marginTop = "mt-20",
	displayField = "name",
	renderOption,
	fetchMoreData = null,
	hasMore = false,
	loader = false,
	selectedItems = "",
	classDropdown = "absolute z-50",
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const dropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const normalizedOptions = Array.isArray(options?.data)
		? options.data
		: Array.isArray(options)
			? options
			: [];

	const filteredOptions =
		normalizedOptions.length > 0
			? normalizedOptions.filter((option) =>
					(option?.[displayField] || "").toLowerCase().includes(searchTerm.toLowerCase()),
				)
			: [];

	const handleSelectChange = (selectedValue, selectedText) => {
		onChange(id, selectedValue, selectedText);
		setIsDropdownOpen(false);
	};

	const handleClear = () => {
		onChange(id, null, null);
		if (typeof onClear === "function") {
			onClear();
		}
	};

	const handleLoadMore = (e) => {
		e.stopPropagation();
		if (fetchMoreData && hasMore) {
			fetchMoreData();
		}
	};

	return (
		<div
			ref={dropdownRef}
			className={`relative flex flex-col ${disabled ? "disabled-select" : ""}`}
		>
			<label
				htmlFor={id}
				className="mb-2 flex items-center text-left font-normal text-gray-400 text-xs"
			>
				{label}
			</label>
			<div
				className="custom-scrollbar flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-600 bg-blue-gray bg-opacity-20 p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray"
				onClick={() => setIsDropdownOpen(!isDropdownOpen)}
			>
				<div className="flex w-full items-center">
					<span className="flex-1 truncate text-left text-xs" title={value ? selectedItems : ""}>
						{value
							? normalizedOptions.find((option) => option?.id === value)?.[displayField] || (
									<span className="text-sm text-yellow-400 italic">
										The selected item may not be visible in the list — hover to view it.
									</span>
								)
							: placeholder}
					</span>
					{value && (
						<FaTimes
							className="ml-2 cursor-pointer text-gray-400"
							onClick={(e) => {
								e.stopPropagation();
								handleClear();
							}}
						/>
					)}
					<FaChevronDown
						className={`ml-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
					/>
				</div>
			</div>
			{isDropdownOpen && (
				<div
					data-testid={`dropdown-menu`}
					className={`${classDropdown} w-full ${marginTop} custom-scrollbar max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-dropdown-bg`}
				>
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => {
							setSearchTerm(e.target.value);
							search(id, e.target.value);
						}}
						placeholder={`Search ${label ? label : ""}`}
						className="w-full border-gray-600 border-b bg-navbar-bg p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-gray-500 focus:ring-gray-500"
					/>
					{loader ? (
						<div className="p-2 text-center text-gray-400">Loading...</div>
					) : filteredOptions.length > 0 ? (
						<>
							{filteredOptions.map((option, i) => {
								const isSelected = option?.id === value;
								return (
									<div
										key={`${option.id}-${i}`}
										onClick={() => handleSelectChange(option?.id, option[displayField])}
										className={`cursor-pointer truncate p-2 text-left text-sm text-white ${isSelected ? "bg-blue-gray bg-opacity-80" : ""}hover:bg-dropdown-hover`}
									>
										{renderOption ? renderOption(option) : option[displayField]}
									</div>
								);
							})}
							{hasMore && (
								<div
									onClick={handleLoadMore}
									className="cursor-pointer p-2 text-center text-blue-gray text-sm hover:text-white"
								>
									See More
								</div>
							)}
						</>
					) : (
						<div className="p-2 text-gray-400">No options found</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SelectInputClient;
